import React, { createContext, useContext, useEffect, useState } from "react"

const ConsentContext = createContext()

const CONSENT_STORAGE_KEY = "rb-privacy-consent"

const defaultConsent = {
  essential: true,
  analytics: false,
  marketing: false,
}

const parseStoredConsent = () => {
  if (typeof window === "undefined") return null
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return { ...defaultConsent, ...parsed, essential: true }
  } catch (err) {
    return null
  }
}

export const ConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(defaultConsent)
  const [gpcEnabled, setGpcEnabled] = useState(false)
  const [dntEnabled, setDntEnabled] = useState(false)
  const [bannerVisible, setBannerVisible] = useState(false)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (typeof window === "undefined") return

    const gpc = window.navigator.globalPrivacyControl === true
    const dnt =
      window.navigator.doNotTrack === "1" ||
      window.navigator.doNotTrack === "yes"

    setGpcEnabled(gpc)
    setDntEnabled(dnt)

    const stored = parseStoredConsent()

    if (stored) {
      // Even if the user previously accepted all, GPC/DNT must override.
      const effective = {
        ...stored,
        analytics: stored.analytics && !gpc && !dnt,
        marketing: stored.marketing && !gpc && !dnt,
      }
      setConsent(effective)
      setBannerVisible(false)
    } else if (gpc || dnt) {
      setConsent(defaultConsent)
      setBannerVisible(false)
      window.localStorage.setItem(
        CONSENT_STORAGE_KEY,
        JSON.stringify(defaultConsent)
      )
    } else {
      setBannerVisible(true)
    }

    setLoaded(true)
  }, [])

  // Expose the effective consent and a tracking loader hook globally so that
  // gatsby-browser can conditionally load GTM without re-importing React.
  useEffect(() => {
    if (typeof window === "undefined") return

    window.__RB_CONSENT__ = consent
    window.__RB_CONSENT_READY__ = true

    if (Array.isArray(window.__RB_CONSENT_CALLBACKS__)) {
      window.__RB_CONSENT_CALLBACKS__.forEach(callback => {
        try {
          callback()
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("Consent callback failed:", err)
        }
      })
      window.__RB_CONSENT_CALLBACKS__ = []
    }

    if (window.__RB_LOAD_TRACKING__ && (consent.analytics || consent.marketing)) {
      window.__RB_LOAD_TRACKING__()
    }

    if (window.dataLayer && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: "consent_update",
        rb_consent_analytics: consent.analytics ? "granted" : "denied",
        rb_consent_marketing: consent.marketing ? "granted" : "denied",
      })
    }
  }, [consent])

  const saveConsent = nextConsent => {
    const effective = {
      ...nextConsent,
      essential: true,
      analytics: nextConsent.analytics && !gpcEnabled && !dntEnabled,
      marketing: nextConsent.marketing && !gpcEnabled && !dntEnabled,
    }

    setConsent(effective)
    setBannerVisible(false)

    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        CONSENT_STORAGE_KEY,
        JSON.stringify(effective)
      )
    }
  }

  const acceptAll = () =>
    saveConsent({ essential: true, analytics: true, marketing: true })
  const rejectAll = () =>
    saveConsent({ essential: true, analytics: false, marketing: false })

  const value = {
    consent,
    gpcEnabled,
    dntEnabled,
    bannerVisible,
    loaded,
    acceptAll,
    rejectAll,
    saveConsent,
    hasAnalyticsConsent: consent.analytics === true,
    hasMarketingConsent: consent.marketing === true,
  }

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  )
}

export const useConsent = () => {
  const context = useContext(ConsentContext)
  if (!context) {
    throw new Error("useConsent must be used within a ConsentProvider")
  }
  return context
}
